import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import classNames from "classnames";
import { AnalyticsId } from "../../constants.js";
import { usePseudoClasses } from "../../hooks/usePseudoClasses.js";
import { useUuid } from "../../hooks/useUuid.js";
import styles from "./styles.module.scss";
import { u as useAnimate } from "../../use-animate.js";
var TogglePosition = /* @__PURE__ */ ((TogglePosition2) => {
  TogglePosition2["left"] = "left";
  TogglePosition2["right"] = "right";
  return TogglePosition2;
})(TogglePosition || {});
var ToggleOnColor = /* @__PURE__ */ ((ToggleOnColor2) => {
  ToggleOnColor2["onwhite"] = "onwhite";
  ToggleOnColor2["onneutral"] = "onneutral";
  ToggleOnColor2["onblueberry"] = "onblueberry";
  return ToggleOnColor2;
})(ToggleOnColor || {});
const Toggle = ({
  checked = false,
  label,
  onChange,
  onColor = "onwhite",
  subLabel,
  togglePosition = "left",
  testId
}) => {
  const [checkedState, setCheckedState] = useState(checked);
  const [showToggleAnimation, setShowToggleAnimation] = useState(false);
  const [scope, animate] = useAnimate();
  const inputId = useUuid();
  const toggleId = useUuid();
  const toggleDotId = useUuid();
  const labelId = useUuid();
  const subLabelId = useUuid();
  const { refObject, isHovered, isActive } = usePseudoClasses(scope);
  const showHoveredStyling = isHovered && !showToggleAnimation;
  const isOnWhite = onColor === "onwhite";
  useEffect(() => {
    if (showToggleAnimation) {
      const timer = setTimeout(() => setShowToggleAnimation(false), 300);
      return () => clearTimeout(timer);
    }
  }, [showToggleAnimation]);
  useEffect(() => {
    setCheckedState(checked);
  }, [checked]);
  useEffect(() => {
    animate("#" + toggleId, { background: getBackgroundColor() }, { duration: 0.2, ease: "easeInOut" });
    animate(
      "#" + toggleDotId,
      { background: checkedState ? "var(--color-action-graphics-ondark)" : "var(--core-color-neutral-700)" },
      { duration: 0.2, ease: "easeInOut" }
    );
    animate("#" + toggleDotId, { x: showHoveredStyling ? 9 : checkedState ? 18 : 0 }, { duration: 0.2, ease: "easeInOut" });
    animate("svg", { opacity: checkedState ? 1 : 0 }, { duration: 0.2, ease: "easeInOut" });
  }, [checkedState, showHoveredStyling, isActive]);
  const getBackgroundColor = () => {
    if (checkedState && isActive) {
      return "var(--core-color-blueberry-800)";
    } else if (checkedState) {
      return showHoveredStyling ? "var(--color-action-graphics-onlight-hover)" : "var(--color-action-graphics-onlight)";
    } else if (isOnWhite && isActive) {
      return "var(--core-color-neutral-400)";
    } else if (isOnWhite) {
      return showHoveredStyling ? "var(--core-color-neutral-200)" : "var(--core-color-neutral-50)";
    } else if (isActive) {
      return "var(--core-color-neutral-200)";
    } else {
      return showHoveredStyling ? "var(--core-color-neutral-50)" : "var(--core-color-white)";
    }
  };
  const handleChange = (event) => {
    setCheckedState(event.target.checked);
    setShowToggleAnimation(true);
    onChange == null ? void 0 : onChange(event);
  };
  const toggleContainerClassNames = classNames(styles["toggle-container"]);
  const toggleRowClassNames = classNames(styles["toggle-container__row"], {
    [styles["toggle-container__row--right"]]: togglePosition === "right"
    /* right */
  });
  const subLabelClassNames = classNames(styles["toggle-container__sublabel"], {
    [styles["toggle-container__sublabel--toggle-right"]]: togglePosition === "right"
    /* right */
  });
  const toggleClassNames = classNames(styles["toggle-container__toggle"], {
    [styles["toggle-container__toggle--ignore-hover"]]: showToggleAnimation,
    [styles["toggle-container__toggle--on-white"]]: onColor === "onwhite"
    /* onwhite */
  });
  const toggleDotClassNames = classNames(styles["toggle-container__toggle__dot"], {
    [styles["toggle-container__toggle__dot--ignore-hover"]]: showToggleAnimation
  });
  const renderToggle = () => /* @__PURE__ */ jsxs("label", { ref: refObject, className: styles["toggle-container__toggle-group"], children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        id: inputId,
        type: "checkbox",
        checked: checkedState,
        onChange: handleChange,
        className: styles["toggle-container__input"],
        "aria-label": label.map((l) => l.text).join(" "),
        "aria-describedby": subLabel ? subLabelId : void 0
      }
    ),
    /* @__PURE__ */ jsx("span", { id: toggleId, className: toggleClassNames, "aria-hidden": "true", children: /* @__PURE__ */ jsx("span", { id: toggleDotId, className: toggleDotClassNames, "aria-hidden": "true", children: /* @__PURE__ */ jsx(
      "svg",
      {
        width: "17",
        height: "13",
        viewBox: "0 0 17 13",
        xmlns: "http://www.w3.org/2000/svg",
        className: styles["toggle-container__toggle__dot__icon"],
        children: /* @__PURE__ */ jsx("path", { d: "M15 2L6.80839 10.548L2 5.53145", fill: "none", strokeWidth: "3" })
      }
    ) }) })
  ] });
  const renderLabelText = () => {
    return /* @__PURE__ */ jsx(
      "span",
      {
        id: labelId,
        className: classNames(styles["toggle-container__label"], {
          [styles["toggle-container__label--toggle-right"]]: togglePosition === "right"
          /* right */
        }),
        children: label.map((labelText) => {
          const labelClassNames = classNames({
            [styles["toggle-container__label__text--subdued"]]: labelText.type === "subdued"
          });
          return /* @__PURE__ */ jsx("span", { className: labelClassNames, children: labelText.text }, labelId + labelText.text);
        })
      }
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: toggleContainerClassNames, "data-testid": testId, "data-analyticsid": AnalyticsId.Toggle, children: [
    /* @__PURE__ */ jsxs("div", { className: toggleRowClassNames, children: [
      togglePosition === "left" && /* @__PURE__ */ jsxs(Fragment, { children: [
        renderToggle(),
        renderLabelText()
      ] }),
      togglePosition === "right" && /* @__PURE__ */ jsxs(Fragment, { children: [
        renderLabelText(),
        renderToggle()
      ] })
    ] }),
    subLabel && /* @__PURE__ */ jsx("div", { id: subLabelId, className: subLabelClassNames, children: subLabel })
  ] });
};
export {
  ToggleOnColor,
  TogglePosition,
  Toggle as default
};
//# sourceMappingURL=index.js.map
